import React from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { Plus, Send, BellRing } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { useTheme } from '../../context/ThemeContext';
import { Button } from '../ui/button';
import { ThemeToggle } from '../ui/ThemeToggle';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import ManageProfiles from '../ManageProfiles';
import UploadModal from '../upload/UploadModal';
import CreatorOnboarding from '../CreatorOnboarding';

// Import sub-components
import { useHeaderState } from './useHeaderState';
import { MobileNav } from './MobileNav';
import { DesktopNav } from './DesktopNav';
import { SearchBar, NavButton } from './SearchBar';
import { ProfileDropdown } from './ProfileDropdown';
import { MobileMenu } from './MobileMenu';
import { announceToScreenReader } from '../../lib/accessibility';

// Rainbow Logo Component
const RainbowLogo = () => (
  <>
    <span className="text-red-500">I</span>
    <span className="text-orange-500 ml-0.5">G</span>
    <span className="text-yellow-500">o</span>
    <span className="text-green-500">t</span>
    <span className="text-blue-500 ml-1">S</span>
    <span className="text-indigo-500">o</span>
    <span className="text-purple-500">u</span>
    <span className="text-pink-500">l</span>
  </>
);

const Header = ({ isLandingPage = false }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout, isAuthenticated } = useAuth();
  const { isDark } = useTheme();

  const {
    isSearchOpen,
    setIsSearchOpen,
    showManageProfiles,
    setShowManageProfiles,
    showUploadModal,
    setShowUploadModal,
    showUpgradeModal,
    setShowUpgradeModal,
    showMobileMenu,
    setShowMobileMenu,
    unreadMessages,
    unreadNotifications,
    handleUploadClick,
    checkSubscriptionStatus
  } = useHeaderState(isAuthenticated);

  const handleLogout = () => {
    logout();
    navigate('/');
    announceToScreenReader('Signed out successfully');
  };

  // Landing page header
  if (isLandingPage) {
    return (
      <header 
        className="fixed top-0 left-0 right-0 z-50 bg-gradient-to-b from-black/90 to-transparent safe-area-top"
        role="banner"
        aria-label="Site header"
      >
        <div className="container mx-auto px-4 py-3 md:py-4 flex items-center justify-between">
          <Link 
            to="/" 
            className="text-xl md:text-2xl font-bold flex"
            aria-label="I Got Soul - Go to homepage"
          >
            <RainbowLogo />
          </Link>
          <Link to="/login">
            <Button 
              className="bg-gradient-to-r from-yellow-400 to-purple-500 hover:from-yellow-500 hover:to-purple-600 text-black font-semibold min-h-[44px] px-5 active:scale-95 transition-transform focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-yellow-400 focus-visible:ring-offset-2"
              data-testid="landing-signin-btn"
              aria-label="Sign in to your account"
            >
              Sign In
            </Button>
          </Link>
        </div>
      </header>
    );
  }

  return (
    <>
      {/* Top Header */}
      <header 
        className={`fixed top-0 left-0 right-0 z-50 backdrop-blur-md border-b safe-area-top ${
          isDark 
            ? 'bg-black/95 border-white/5' 
            : 'bg-white/95 border-gray-200'
        }`}
        data-testid="main-header"
        role="banner"
        aria-label="Site header"
      >
        <div className="container mx-auto px-3 md:px-4">
          <div className="flex items-center justify-between h-14 md:h-16">
            {/* Left: Logo + Mobile Menu */}
            <div className="flex items-center gap-2">
              {/* Mobile Menu Button */}
              <MobileMenu 
                isOpen={showMobileMenu}
                setIsOpen={setShowMobileMenu}
                isDark={isDark}
                user={user}
                onLogout={handleLogout}
              />

              {/* Logo */}
              <Link 
                to="/browse" 
                className="text-lg md:text-xl font-bold flex focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-yellow-400 rounded"
                aria-label="I Got Soul - Go to home"
              >
                <RainbowLogo />
              </Link>

              {/* Desktop Navigation */}
              <DesktopNav isDark={isDark} />
            </div>

            {/* Right: Search + Desktop Icons */}
            <div className="flex items-center gap-1 md:gap-2" role="group" aria-label="Quick actions">
              {/* Search */}
              <SearchBar 
                isOpen={isSearchOpen}
                setIsOpen={setIsSearchOpen}
                isDark={isDark}
              />

              {/* Desktop only icons */}
              <div className="hidden md:flex items-center gap-1" role="group" aria-label="Desktop quick actions">
                {/* Theme Toggle - Desktop */}
                <ThemeToggle 
                  variant="ghost"
                  className={isDark 
                    ? 'text-gray-300 hover:text-white hover:bg-white/10' 
                    : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                  }
                />
                
                <NavButton
                  icon={Plus}
                  onClick={handleUploadClick}
                  tooltip="Upload Content"
                  ariaLabel="Upload new content"
                  testId="upload-btn-desktop"
                  isDark={isDark}
                />
                <NavButton
                  icon={Send}
                  onClick={() => {
                    navigate('/messages');
                    announceToScreenReader('Navigated to Messages');
                  }}
                  badge={unreadMessages}
                  tooltip="Messages"
                  ariaLabel={`Messages${unreadMessages > 0 ? `, ${unreadMessages} unread` : ''}`}
                  isActive={location.pathname === '/messages'}
                  testId="messages-btn-desktop"
                  isDark={isDark}
                />
                <NavButton
                  icon={BellRing}
                  onClick={() => announceToScreenReader('Notifications panel coming soon')}
                  badge={unreadNotifications}
                  tooltip="Notifications"
                  ariaLabel={`Notifications${unreadNotifications > 0 ? `, ${unreadNotifications} unread` : ''}`}
                  testId="notifications-btn-desktop"
                  isDark={isDark}
                />
              </div>

              {/* Profile Dropdown - Desktop */}
              <ProfileDropdown 
                user={user}
                isDark={isDark}
                onLogout={handleLogout}
                onManageProfiles={() => setShowManageProfiles(true)}
              />
            </div>
          </div>
        </div>
      </header>

      {/* Mobile Bottom Navigation */}
      <MobileNav 
        isDark={isDark}
        unreadMessages={unreadMessages}
        unreadNotifications={unreadNotifications}
        onUploadClick={handleUploadClick}
      />

      {/* Modals */}
      <Dialog open={showManageProfiles} onOpenChange={setShowManageProfiles}>
        <DialogContent className="max-w-4xl bg-gray-900 text-white border-gray-700">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold flex">
              <span className="text-red-500">M</span>
              <span className="text-orange-500">a</span>
              <span className="text-yellow-500">n</span>
              <span className="text-green-500">a</span>
              <span className="text-blue-500">g</span>
              <span className="text-indigo-500">e</span>
              <span className="text-white ml-2">Profiles</span>
            </DialogTitle>
          </DialogHeader>
          <ManageProfiles onClose={() => setShowManageProfiles(false)} />
        </DialogContent>
      </Dialog>

      <UploadModal
        isOpen={showUploadModal}
        onClose={() => setShowUploadModal(false)}
        onUploadComplete={() => window.location.reload()}
      />

      <CreatorOnboarding
        isOpen={showUpgradeModal}
        onClose={() => setShowUpgradeModal(false)}
        onComplete={() => {
          checkSubscriptionStatus();
          setShowUpgradeModal(false);
        }}
      />
    </>
  );
};

export default Header;
